import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const DashboardContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 32px;
  
  .greeting {
    h1 {
      font-size: 28px;
      font-weight: 600;
      color: ${props => props.theme.colors.primary};
      margin: 0 0 8px 0;
    }
    
    .subtitle {
      color: ${props => props.theme.colors.textLight};
      font-size: 16px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .date {
      color: ${props => props.theme.colors.secondary};
      font-weight: 500;
    }
  }
  
  .sync-status {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: rgba(76, 175, 80, 0.1);
    border-radius: 20px;
    font-size: 14px;
    color: #4CAF50;
    
    .indicator {
      width: 8px;
      height: 8px;
      background: #4CAF50;
      border-radius: 50%;
      animation: pulse 2s infinite;
    }
  }
  
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainMetrics = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 16px;
  border: 1px solid ${props => props.theme.colors.border};
  overflow: hidden;
`;

const MetricHeader = styled.div`
  padding: 24px 24px 0 24px;
  
  h2 {
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 16px 0;
    color: ${props => props.theme.colors.primary};
  }
`;

const MacroSection = styled.div`
  padding: 0 24px 24px 24px;
  
  .macro-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
`;

const MacroCard = styled.div`
  text-align: center;
  
  .label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${props => props.theme.colors.textLight};
    margin-bottom: 8px;
    font-weight: 500;
  }
  
  .progress-ring {
    position: relative;
    width: 80px;
    height: 80px;
    margin: 0 auto 12px;
  }
  
  .value {
    font-size: 18px;
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    line-height: 1;
  }
  
  .target {
    font-size: 12px;
    color: ${props => props.theme.colors.textLight};
  }
`;

const ProgressRing = styled.svg`
  width: 80px;
  height: 80px;
  transform: rotate(-90deg);
  
  .background {
    fill: none;
    stroke: ${props => props.theme.colors.border};
    stroke-width: 6;
  }
  
  .progress {
    fill: none;
    stroke: ${props => props.color || props.theme.colors.secondary};
    stroke-width: 6;
    stroke-linecap: round;
    transition: stroke-dasharray 0.5s ease;
  }
  
  .value-text {
    transform: rotate(90deg);
    text-anchor: middle;
    dominant-baseline: central;
    font-size: 14px;
    font-weight: 700;
    fill: ${props => props.theme.colors.primary};
  }
`;

const StatsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 20px;
  
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    
    .title {
      font-size: 14px;
      font-weight: 500;
      color: ${props => props.theme.colors.textLight};
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    .icon {
      font-size: 20px;
    }
  }
  
  .main-value {
    font-size: 32px;
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    line-height: 1;
    margin-bottom: 8px;
  }
  
  .sub-info {
    font-size: 14px;
    color: ${props => props.theme.colors.textLight};
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .trend {
      display: flex;
      align-items: center;
      gap: 4px;
      font-weight: 500;
      
      &.positive { color: #4CAF50; }
      &.negative { color: #f44336; }
    }
  }
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ActionSection = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 16px;
  border: 1px solid ${props => props.theme.colors.border};
  padding: 24px;
  
  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 20px 0;
    color: ${props => props.theme.colors.primary};
  }
  
  .actions-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const ActionButton = styled.button`
  background: ${props => props.variant === 'primary' ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.variant === 'primary' ? 'white' : props.theme.colors.primary};
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: 8px;
  padding: 16px;
  text-align: left;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.medium};
  }
  
  .icon {
    font-size: 18px;
    margin-bottom: 6px;
    display: block;
  }
  
  .text {
    font-size: 14px;
    font-weight: 500;
  }
`;

const RecentActivity = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 16px;
  border: 1px solid ${props => props.theme.colors.border};
  padding: 24px;
  
  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 20px 0;
    color: ${props => props.theme.colors.primary};
  }
  
  .activity-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .activity-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: ${props => props.theme.colors.background};
    border-radius: 8px;
    
    .icon {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: ${props => props.theme.colors.secondary};
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
    }
    
    .content {
      flex: 1;
      
      .action {
        font-weight: 500;
        color: ${props => props.theme.colors.primary};
        font-size: 14px;
        margin-bottom: 2px;
      }
      
      .time {
        font-size: 12px;
        color: ${props => props.theme.colors.textLight};
      }
    }
    
    .value {
      font-weight: 600;
      color: ${props => props.theme.colors.secondary};
      font-size: 14px;
    }
  }
`;

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    calories: { current: 820, target: 2000 },
    protein: { current: 60, target: 140 },
    carbs: { current: 97, target: 200 },
    fat: { current: 24, target: 70 },
    weight: { current: 153.4, change: -0.3, target: 154 },
    activeMinutes: { current: 40, target: 60 },
    hydration: { current: 48, target: 64 }
  });

  const [recentActivity] = useState([
    { action: 'Logged breakfast', time: '8:30 AM', value: '420 kcal', icon: '🍳' },
    { action: 'Morning workout', time: '7:00 AM', value: '45 min', icon: '💪' },
    { action: 'Weight check-in', time: '6:45 AM', value: '153.4 lbs', icon: '⚖️' },
    { action: 'Sleep tracking', time: '11:00 PM', value: '7.2 hrs', icon: '😴' }
  ]);

  const getCurrentDate = () => {
    return new Date().toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const calculateProgress = (current, target) => {
    return Math.min((current / target) * 100, 100);
  };

  const createProgressPath = (percentage) => {
    const radius = 34;
    const circumference = 2 * Math.PI * radius;
    const progress = (percentage / 100) * circumference;
    return `${progress} ${circumference}`;
  };

  const getMacroColor = (macro) => {
    const colors = {
      protein: '#FF6B6B',
      carbs: '#4ECDC4', 
      fat: '#FFD93D'
    };
    return colors[macro] || '#DAA520';
  };

  return (
    <DashboardContainer>
      <HeaderSection>
        <div className="greeting">
          <h1>Performance Dashboard</h1>
          <div className="subtitle">
            <span className="date">{getCurrentDate()}</span> 
            • Target: Lean Maintenance Phase
          </div>
        </div>
        <div className="sync-status">
          <div className="indicator"></div>
          System Operational
        </div>
      </HeaderSection>

      <MetricsGrid>
        <MainMetrics>
          <MetricHeader>
            <h2>Macro Distribution</h2>
          </MetricHeader>
          <MacroSection>
            <div className="macro-grid">
              {['protein', 'carbs', 'fat'].map((macro) => (
                <MacroCard key={macro}>
                  <div className="label">{macro}</div>
                  <div className="progress-ring">
                    <ProgressRing color={getMacroColor(macro)}>
                      <circle
                        className="background"
                        cx="40"
                        cy="40"
                        r="34"
                      />
                      <circle
                        className="progress"
                        cx="40"
                        cy="40"
                        r="34"
                        strokeDasharray={createProgressPath(calculateProgress(dashboardData[macro].current, dashboardData[macro].target))}
                      />
                      <text
                        x="40"
                        y="40"
                        className="value-text"
                      >
                        {dashboardData[macro].current}
                      </text>
                    </ProgressRing>
                  </div>
                  <div className="value">{dashboardData[macro].current}g</div>
                  <div className="target">of {dashboardData[macro].target}g</div>
                </MacroCard>
              ))}
            </div>
          </MacroSection>
        </MainMetrics>

        <StatsPanel>
          <StatCard>
            <div className="header">
              <div className="title">Current Weight</div>
              <div className="icon">⚖️</div>
            </div>
            <div className="main-value">{dashboardData.weight.current}</div>
            <div className="sub-info">
              <span>lbs</span>
              <span className={`trend ${dashboardData.weight.change < 0 ? 'positive' : 'negative'}`}>
                {dashboardData.weight.change > 0 ? '↗' : '↘'} {Math.abs(dashboardData.weight.change)} lbs
              </span>
            </div>
          </StatCard>

          <StatCard>
            <div className="header">
              <div className="title">Total Calories</div>
              <div className="icon">🔥</div>
            </div>
            <div className="main-value">{dashboardData.calories.current}</div>
            <div className="sub-info">
              <span>of {dashboardData.calories.target} kcal</span>
              <span className="trend positive">
                {Math.round(calculateProgress(dashboardData.calories.current, dashboardData.calories.target))}%
              </span>
            </div>
          </StatCard>

          <StatCard>
            <div className="header">
              <div className="title">Active Minutes</div>
              <div className="icon">⏱️</div>
            </div>
            <div className="main-value">{dashboardData.activeMinutes.current}</div>
            <div className="sub-info">
              <span>of {dashboardData.activeMinutes.target} min</span>
              <span className="trend positive">
                {Math.round(calculateProgress(dashboardData.activeMinutes.current, dashboardData.activeMinutes.target))}%
              </span>
            </div>
          </StatCard>
        </StatsPanel>
      </MetricsGrid>

      <SectionGrid>
        <ActionSection>
          <h3>Quick Actions</h3>
          <div className="actions-grid">
            <ActionButton variant="primary">
              <span className="icon">🍽️</span>
              <span className="text">Log Meal</span>
            </ActionButton>
            <ActionButton>
              <span className="icon">💪</span>
              <span className="text">Start Workout</span>
            </ActionButton>
            <ActionButton>
              <span className="icon">📊</span>
              <span className="text">View Analytics</span>
            </ActionButton>
            <ActionButton>
              <span className="icon">💧</span>
              <span className="text">Log Hydration</span>
            </ActionButton>
          </div>
        </ActionSection>

        <RecentActivity>
          <h3>Recent Activity</h3>
          <div className="activity-list">
            {recentActivity.map((activity, index) => (
              <div key={index} className="activity-item">
                <div className="icon">{activity.icon}</div>
                <div className="content">
                  <div className="action">{activity.action}</div>
                  <div className="time">{activity.time}</div>
                </div>
                <div className="value">{activity.value}</div>
              </div>
            ))}
          </div>
        </RecentActivity>
      </SectionGrid>
    </DashboardContainer>
  );
};

export default Dashboard;